// Keeps the SidebarPanelRegistry in sync with the plugin host's declared
// activity-bar items: every `contributes.activityBar` entry of every enabled
// plugin becomes a bottom-placed sidebar panel whose body is a PluginActivityView
// (the container's surface + tree views). Subscribes to contribution changes so
// install/enable/disable add or remove panels live. Returns a disposer that
// unregisters every panel it created.

import { Puzzle } from 'lucide-react';
import type { ReactElement } from 'react';
import { SidebarPanelRegistry, type SidebarPanelDef } from '../sidebar/SidebarPanelRegistry';
import { resolveContributionIcon } from '../tools/contributionIcon';
import { PluginActivityView } from './PluginActivityView';
import type { PluginSystem } from './setup';

export function registerPluginContributedPanels(system: PluginSystem): () => void {
  const host = system.host;
  const registered = new Map<string, () => void>();

  const sync = (): void => {
    const items = host.listActivityBarItems();
    const desired = new Map(items.map((i) => [`plugin:${i.pluginId}:${i.id}`, i]));

    // Remove panels whose contribution is gone (uninstall/disable).
    for (const [key, unregister] of [...registered]) {
      if (!desired.has(key)) {
        unregister();
        registered.delete(key);
      }
    }

    // Add panels for newly-visible contributions. Order them after the built-in
    // and Plugins panels (which sit at <= 50), grouped at the rail bottom.
    let order = 60;
    for (const [key, item] of desired) {
      order += 1;
      if (registered.has(key)) continue;
      const Icon = resolveContributionIcon(item.icon) ?? Puzzle;
      const def: SidebarPanelDef = {
        id: key,
        title: item.title,
        icon: Icon,
        order,
        placement: 'bottom',
        Component: (): ReactElement => (
          <PluginActivityView system={system} pluginId={item.pluginId} containerId={item.id} />
        ),
      };
      registered.set(key, SidebarPanelRegistry.register(def));
    }
  };

  sync();
  const unsub = host.onDidChangeContributions(sync);
  return () => {
    unsub();
    for (const unregister of registered.values()) unregister();
    registered.clear();
  };
}
